import React from "react";
import { useApplication } from "@/contexts/ApplicationContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApplicationStage } from "@/hooks/useApplicationStage";
import ProgressSection from "./status/ProgressSection";
import StageCard from "./status/StageCard";
import NextStepsSection from "./status/NextStepsSection";
import { useActiveJob } from "@/contexts/ActiveJobContext";

const ApplicationStatus: React.FC = () => {
  const { application } = useApplication();
  const { getStageStatus, progress, getNextSteps, getContinueUrl } =
    useApplicationStage(application);
  const { job } = useActiveJob();

  const getStageDetails = (stage: string): string | undefined => {
    switch (stage) {
      case "driver-onroad-test":
        return application.assessmentResults.driverOnroadTestScore
          ? `Driver Onroad Test Score: ${application.assessmentResults.driverOnroadTestScore}%`
          : undefined;
      case "documents":
        return application.documents.drivingLicenceNumber
          ? `DVLA share code submitted`
          : undefined;
      case "orientation":
        return application.orientation.date
          ? `Scheduled for ${new Date(
              application.orientation.date
            ).toLocaleDateString("en-GB", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
            })}`
          : undefined;
      case "orientation-quiz":
        return application.orientation.quizScore
          ? `Quiz Score: ${application.orientation.quizScore}%`
          : undefined;
      case "personal-details":
        return application.personalDetails
          ? `Personal details completed`
          : undefined;
      case "trial":
        return application.formsCompleted.vehicleReleaseForm
          ? `Vehicle release form completed`
          : undefined;
      case "contract":
        return application.formsCompleted.driverContract
          ? `Contract signed`
          : undefined;
      case "vehicle-collection":
        return application.vehicleCollection?.date
          ? `Vehicle collection scheduled for ${new Date(
              application.vehicleCollection.date
            ).toLocaleDateString("en-GB", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}`
          : undefined;
      default:
        return undefined;
    }
  };

  const steps = [
    {
      title: "Personal Information",
      description: "Basic details and eligibility check",
      stages: ["basic-details"],
    },
    {
      title: "Assessment",
      description: "Situational judgment test",
      stages: ["driver-onroad-test"],
    },
    {
      title: "Document Verification",
      description: "DVLA and right to work checks",
      stages: ["documents"],
    },
    {
      title: "Orientation",
      description: "In-person orientation session and personal details",
      stages: ["orientation", "orientation-quiz", "personal-details"],
    },
    {
      title: "Trial Period",
      description: "Vehicle release form and trial period",
      stages: ["trial"],
    },
    {
      title: "Final Onboarding",
      description: "Contract signing, vehicle collection, and completion",
      stages: ["contract", "vehicle-collection"],
    },
  ].filter((step) =>
    step.stages.some((stage) =>
      job?.stages?.some((jobStage) => jobStage.id === stage)
    )
  );

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-0">
      <Card className="mb-6">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg md:text-xl">
            Application Status
          </CardTitle>
          <CardDescription className="text-sm">
            Application ID: {application.applicationId}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 md:space-y-6">
            <ProgressSection progress={progress} />

            <div className="space-y-3 md:space-y-4">
              {steps.map((step) => {
                // Determine step status: if any stage in the step is current, the step is current
                const stepStatus = step.stages.some(
                  (stage) => application.currentStage === stage
                )
                  ? "current"
                  : step.stages.every(
                      (stage) => getStageStatus(stage) === "completed"
                    )
                  ? "completed"
                  : "upcoming";

                return (
                  <StageCard
                    key={step.title}
                    title={step.title}
                    status={stepStatus}
                    description={step.description}
                    details={getStageDetails(step.stages[0])}
                  />
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <NextStepsSection
        nextSteps={getNextSteps()}
        continueUrl={getContinueUrl()}
      />
    </div>
  );
};

export default ApplicationStatus;
